import ScannedColis from "@/components/colis/ScannedColis";
import ProductCounter from "@/components/product-counter/ProductCounter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Colis, RtoBulkScanReturn } from "@/interfaces/Return";
import { cn } from "@/lib/utils";
import React from "react";

type ColisFilter = "scanned" | "remaining";

interface BulkReturnDetailDialogProps {
  return_: RtoBulkScanReturn | null;
  scannedColis: Colis[];
  remainingColis: Colis[];
  scannedColisCount: number;
  expectedColisCount: number;
  isComplete: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const BulkReturnDetailDialog: React.FC<BulkReturnDetailDialogProps> = ({
  return_,
  scannedColis,
  remainingColis,
  scannedColisCount,
  expectedColisCount,
  isComplete,
  isOpen,
  onClose,
}) => {
  const [filter, setFilter] = React.useState<ColisFilter>("scanned");

  React.useEffect(() => {
    if (isOpen) {
      setFilter("scanned");
    }
  }, [isOpen, return_?.numTournee]);

  if (!return_) {
    return null;
  }

  const visibleColis = filter === "scanned" ? scannedColis : remainingColis;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2 pr-6">
            <DialogTitle>
              Tournée N° {return_.numTournee}
              {return_.numDemande != null && ` - Demande ${return_.numDemande}`}
            </DialogTitle>
            <span
              className={cn(
                "text-xs font-semibold shrink-0 rounded-md px-2 py-1",
                isComplete
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-gray-100 text-gray-700"
              )}
            >
              {scannedColisCount} / {expectedColisCount} colis
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-gray-700">{return_.sellerName}</p>
            {return_.countArticles != null && (
              <ProductCounter productCount={return_.countArticles} />
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant={filter === "scanned" ? "default" : "outline"}
              className="flex-1"
              onClick={() => setFilter("scanned")}
            >
              Colis scannés ({scannedColis.length})
            </Button>
            <Button
              type="button"
              variant={filter === "remaining" ? "default" : "outline"}
              className="flex-1"
              onClick={() => setFilter("remaining")}
            >
              Colis restants ({remainingColis.length})
            </Button>
          </div>

          <ScrollArea className={cn("max-h-[45vh] pr-2")} showScrollIndicator>
            {visibleColis.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {filter === "scanned"
                  ? "Aucun colis scanné"
                  : "Aucun colis restant"}
              </p>
            ) : (
              visibleColis.map((colis, index) => (
                <ScannedColis
                  key={`${colis.colisCode}-${index}`}
                  colis={colis}
                  isScanned={filter === "scanned"}
                />
              ))
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkReturnDetailDialog;
